// Drill-to-focus navigation — PURE + tested.
//
// Clicking a row re-scopes the WHOLE page (KPI tiles, trend, campaign-type split,
// search terms) to that entity and collapses the table to its children. A
// breadcrumb walks back up. Plain click drills, so there is no separate "isolate"
// mode to learn — but that also means the drill must be exactly right, because
// it is the same gesture as "look at this row".

import type {
  GoogleAdsFocus,
  GoogleAdsLevel,
  GoogleAdsTreeRow,
} from '../../types/googleAdsReporting';

/** The level a row's children live at. An ad is a leaf and cannot be drilled. */
export function childLevel(level: GoogleAdsLevel): GoogleAdsLevel | null {
  switch (level) {
    case 'location':
      return 'campaign';
    case 'campaign':
      return 'adGroup';
    case 'adGroup':
      return 'ad';
    case 'ad':
      return null;
  }
}

/** Human labels for the level a table is currently showing. */
export const LEVEL_LABEL: Record<GoogleAdsLevel, string> = {
  location: 'Location',
  campaign: 'Campaign',
  adGroup: 'Ad group',
  ad: 'Ad',
};

export const LEVEL_CHILDREN_LABEL: Record<GoogleAdsLevel, string> = {
  location: 'Locations',
  campaign: 'Campaigns',
  adGroup: 'Ad groups',
  ad: 'Ads',
};

/**
 * The focus produced by clicking a row.
 *
 * ⚠️ The top level focuses the kyck LOCATION (`row.accountId`), not the Google
 * customer — focusing the customer would drag in every other location sharing
 * that Google account, which is the mis-attribution the per-campaign token
 * matching exists to prevent.
 *
 * Returns null for an ad — it has no children, so drilling would blank the table
 * while re-scoping everything else, which reads as a bug. The row stays clickable
 * for selection but does not change focus. Also returns null when a row carries
 * no Location at all, so a click can never produce a focus that matches nothing.
 */
export function focusForRow(row: GoogleAdsTreeRow): GoogleAdsFocus | null {
  if (row.level === 'ad') return null;
  const accountId = row.accountId;
  if (!accountId) return null;
  return {
    level: row.level,
    accountId,
    campaignId: row.level === 'location' ? null : row.campaignId,
    adGroupId: row.level === 'adGroup' ? row.id : null,
    adId: null,
  };
}

/** One breadcrumb step. `focus` is null for the root ("All accounts"). */
export interface Crumb {
  label: string;
  focus: GoogleAdsFocus | null;
}

/**
 * The breadcrumb path for a focus. Names come from the rows the user clicked
 * through (the caller remembers them), so a crumb reads "Anytime Fitness Cromer ›
 * Kyck | PMAX | Cromer" rather than a Google account name and an opaque id.
 */
export function breadcrumbs(
  focus: GoogleAdsFocus | null,
  names: { location?: string | null; campaign?: string | null; adGroup?: string | null },
): Crumb[] {
  const crumbs: Crumb[] = [{ label: 'All locations', focus: null }];
  if (!focus) return crumbs;

  crumbs.push({
    label: names.location ?? focus.accountId,
    focus: { level: 'location', accountId: focus.accountId },
  });
  if (focus.level === 'location') return crumbs;

  if (focus.campaignId) {
    crumbs.push({
      label: names.campaign ?? focus.campaignId,
      focus: {
        level: 'campaign',
        accountId: focus.accountId,
        campaignId: focus.campaignId,
      },
    });
  }
  if (focus.level === 'campaign') return crumbs;

  if (focus.adGroupId) {
    crumbs.push({
      label: names.adGroup ?? focus.adGroupId,
      focus: {
        level: 'adGroup',
        accountId: focus.accountId,
        campaignId: focus.campaignId,
        adGroupId: focus.adGroupId,
      },
    });
  }
  return crumbs;
}

/**
 * Which ATTRIBUTE columns make sense at a level. Rendering an ad-group's "Budget"
 * or an ad's "Campaign type" as blank cells wastes the width the metrics need, so
 * the table swaps its attribute columns per level rather than showing a fixed set
 * that is mostly empty.
 *
 * ⚠️ `status` IS DELIBERATELY ABSENT (2026-08-02). It is no longer a column at
 * all — it renders as an inline coloured dot before the entity name, so it costs
 * no width and is never toggled off. A Location row still has no status of its
 * own and gets no dot.
 */
export function attributeColumnsFor(level: GoogleAdsLevel): string[] {
  switch (level) {
    // A Location has no delivery status / budget / bid strategy of its own —
    // those are campaign settings, and showing one campaign's value as if it
    // were the Location's would be a fabrication. It gets the Google account it
    // bills through instead (secondary, but genuinely useful to a specialist).
    case 'location':
      return ['googleAccount'];
    case 'campaign':
      return ['googleAccount', 'channelType', 'biddingStrategyType', 'targetRoas', 'budget'];
    case 'adGroup':
      return ['targetCpa', 'targetRoas'];
    case 'ad':
      return ['adType', 'approvalStatus', 'adStrength', 'finalUrl'];
  }
}

/**
 * Whether impression-share metrics can be measured at this level.
 *
 * ⚠️ This build's competitiveness view is CAMPAIGN-grain (click share is
 * campaign-only in Google regardless), so ad-group and ad rows carry no share
 * data. Saying so explicitly beats rendering a column of "—" with no explanation
 * — and beats rendering 0%, which would be a fabricated measurement.
 */
export function levelHasImpressionShare(level: GoogleAdsLevel): boolean {
  // A Location row is a sum of its campaigns' rows, so it inherits campaign-grain
  // share exactly.
  return level === 'location' || level === 'campaign';
}
